<template>
  <div class="skill-studio-mask" @click.self="emit('close')">
    <section class="skill-studio" aria-label="Skill 工作室">
      <header class="studio-header">
        <div>
          <h2>Skill 工作室</h2>
          <p>管理个人技能、本地导入与发布（对话创建请在右侧 AI 对话框完成）</p>
        </div>
        <button type="button" class="close-btn" aria-label="关闭" @click="emit('close')">×</button>
      </header>

      <div class="studio-body">
        <aside class="skill-list-pane">
          <div class="list-actions">
            <button type="button" class="primary" @click="startCreateViaChat">与 AI 对话创建</button>
            <button type="button" @click="startImport">上传本地技能</button>
          </div>
          <ul v-if="skills.length" class="skill-list">
            <li
              v-for="skill in skills"
              :key="skill.id"
              :class="{ active: selectedId === skill.id && mode === 'list' }"
              @click="selectSkill(skill.id)"
            >
              <strong>{{ skill.name }}</strong>
              <span>{{ skill.version }} · {{ statusLabel(skill.status) }}</span>
            </li>
          </ul>
          <p v-else class="empty">暂无 Skill，请先创建或导入</p>
        </aside>

        <section class="skill-detail-pane">
          <template v-if="mode === 'import'">
            <h3>上传本地技能</h3>
            <p class="hint">支持上传标准 Skill ZIP 包（含 skill.yaml / SKILL.md / skill-spec.yaml），也兼容粘贴 JSON；或填写源 Skill ID 进行克隆。导入后进入隔离校验并生成 Private Draft。</p>

            <div class="import-tabs">
              <button type="button" :class="{ active: importTab === 'package' }" @click="importTab = 'package'">
                Skill 包
              </button>
              <button type="button" :class="{ active: importTab === 'clone' }" @click="importTab = 'clone'">
                克隆已有
              </button>
            </div>

            <template v-if="importTab === 'package'">
              <label class="file-upload">
                选择 .zip / .json 文件
                <input type="file" accept=".zip,.json,application/zip,application/json,text/plain" @change="onPackageFile" />
              </label>
              <p v-if="importFileName" class="hint">已选择：{{ importFileName }}</p>
              <label>
                或粘贴 JSON
                <textarea
                  v-model="importForm.packageContent"
                  rows="10"
                  placeholder='标准 ZIP 请直接上传；JSON 示例：{"code":"sor_generate","name":"SOR 生成","promptText":"...","sopText":"..."}'
                />
              </label>
              <label>覆盖编码（可选）<input v-model="importForm.code" placeholder="留空则使用包内 code" /></label>
              <label>覆盖名称（可选）<input v-model="importForm.name" /></label>
            </template>
            <template v-else>
              <label>源 Skill ID<input v-model="importForm.sourceSkillId" placeholder="数字 ID" /></label>
              <label>新编码（可选）<input v-model="importForm.code" /></label>
              <label>新名称（可选）<input v-model="importForm.name" /></label>
            </template>

            <div v-if="importWarnings.length" class="warn-box">
              <div v-for="(w, i) in importWarnings" :key="i">{{ w }}</div>
            </div>

            <div class="form-actions">
              <button type="button" class="primary" :disabled="saving" @click="handleImport">
                {{ saving ? '导入中…' : '导入并校验' }}
              </button>
            </div>
          </template>

          <template v-else-if="detail">
            <h3>{{ detail.name }}</h3>
            <p class="meta">
              {{ detail.code }} · {{ detail.currentVersion || detail.status }} · {{ detail.visibility }} ·
              {{ statusLabel(detail.status) }}
            </p>
            <p class="meta package-meta">
              <span v-if="detail.hasStandardPackage" class="badge ok">标准包已就绪</span>
              <span v-else class="badge warn">将在导出/发布时合成标准包</span>
              <span v-if="detail.packageSchemaVersion">{{ detail.packageSchemaVersion }}</span>
              <span v-if="detail.acceptanceCaseCount != null">
                验收用例 {{ detail.acceptanceCaseCount }} 条
              </span>
            </p>
            <p>{{ detail.description }}</p>
            <h4 class="section-title">Prompt</h4>
            <pre v-if="detail.promptText" class="prompt-block">{{ detail.promptText }}</pre>
            <p v-else class="empty">暂无 Prompt</p>
            <h4 v-if="detail.sopText" class="section-title">SOP</h4>
            <pre v-if="detail.sopText" class="prompt-block">{{ detail.sopText }}</pre>
            <div v-if="acceptanceResult" class="accept-box">
              <strong>
                验收 {{ acceptanceResult.passed }}/{{ acceptanceResult.total }} 通过
                <template v-if="acceptanceResult.failed">（失败 {{ acceptanceResult.failed }}）</template>
                · {{ acceptanceResult.provider || '' }}
              </strong>
              <ul v-if="acceptanceResult.cases?.length">
                <li v-for="c in acceptanceResult.cases" :key="c.id || c.description">
                  <span :class="['case-status', c.status]">{{ c.status }}</span>
                  {{ c.id }} — 期望 {{ c.expectedDecisionCode || '-' }} / 实际
                  {{ c.observedDecisionCode || '-' }}
                </li>
              </ul>
              <div v-for="(w, i) in acceptanceResult.warnings || []" :key="'aw-' + i" class="hint">{{ w }}</div>
            </div>
            <div class="form-actions">
              <button type="button" @click="startOptimizeViaChat">用 AI 优化</button>
              <button type="button" :disabled="exporting" @click="handleExport">
                {{ exporting ? '导出中…' : '导出标准包' }}
              </button>
              <button type="button" :disabled="accepting" @click="handleAcceptanceRun">
                {{ accepting ? '验收中…' : '运行验收用例' }}
              </button>
              <button
                v-if="detail.status === 'draft'"
                type="button"
                class="primary"
                @click="handlePublish"
              >
                发布
              </button>
              <button
                v-if="detail.status === 'published'"
                type="button"
                @click="handleDisable"
              >
                停用
              </button>
              <button type="button" class="primary" @click="emit('select', detail)">在 AI 中使用</button>
            </div>
          </template>

          <template v-else>
            <p class="empty">选择左侧 Skill，或点击「与 AI 对话创建 / 本地导入」</p>
          </template>
        </section>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  downloadDocumentSkillPackage,
  getDocumentSkill,
  importDocumentSkill,
  importDocumentSkillPackageFile,
  listDocumentSkills,
  publishDocumentSkill,
  runDocumentSkillAcceptance,
  updateDocumentSkillStatus,
} from '@/api/documentWorkbenchApi';
import type {
  DocumentSkillDetail,
  DocumentSkillListItem,
  SkillAcceptanceRunResult,
} from '@/types/documentWorkbench';
import { formatApiErrorMessage } from '@/api/http';

const props = defineProps<{
  projectId: string;
  /** 打开时进入的模式 */
  initialMode?: 'list' | 'create' | 'import';
}>();
const emit = defineEmits<{
  close: [];
  select: [skill: DocumentSkillDetail | DocumentSkillListItem];
  createViaChat: [];
  optimizeViaChat: [skillId: string];
}>();

const skills = ref<DocumentSkillListItem[]>([]);
const selectedId = ref('');
const detail = ref<DocumentSkillDetail | null>(null);
const mode = ref<'list' | 'import'>(props.initialMode === 'import' ? 'import' : 'list');
const saving = ref(false);

const importTab = ref<'package' | 'clone'>('package');
const importForm = reactive({
  code: '',
  name: '',
  sourceSkillId: '',
  packageContent: '',
});
const importWarnings = ref<string[]>([]);
const importFileName = ref('');
const importZipFile = ref<File | null>(null);
const exporting = ref(false);
const accepting = ref(false);
const acceptanceResult = ref<SkillAcceptanceRunResult | null>(null);

function statusLabel(status?: string) {
  const map: Record<string, string> = {
    draft: '草稿',
    published: '已发布',
    disabled: '已停用',
    archived: '已归档',
  };
  return map[String(status || '')] || status || '';
}

function startCreateViaChat() {
  emit('createViaChat');
  emit('close');
}

function startOptimizeViaChat() {
  const id = detail.value?.id || selectedId.value;
  if (!id) {
    ElMessage.warning('请先选择要优化的技能');
    return;
  }
  emit('optimizeViaChat', id);
  emit('close');
}

function startImport() {
  mode.value = 'import';
  selectedId.value = '';
  detail.value = null;
  importWarnings.value = [];
  importFileName.value = '';
  importZipFile.value = null;
}

async function reload() {
  skills.value = await listDocumentSkills(props.projectId);
}

async function selectSkill(id: string) {
  selectedId.value = id;
  mode.value = 'list';
  acceptanceResult.value = null;
  try {
    detail.value = await getDocumentSkill(id);
  } catch {
    detail.value = skills.value.find((s) => s.id === id) as DocumentSkillDetail;
  }
}

function onPackageFile(ev: Event) {
  const input = ev.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  importFileName.value = file.name;
  importZipFile.value = null;
  importForm.packageContent = '';
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.zip')) {
    importZipFile.value = file;
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    importForm.packageContent = String(reader.result || '');
  };
  reader.readAsText(file);
}

async function handleExport() {
  if (!detail.value?.id) return;
  exporting.value = true;
  try {
    await downloadDocumentSkillPackage(detail.value.id, detail.value.name);
    ElMessage.success('标准 Skill 包已开始下载');
    await selectSkill(detail.value.id);
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  } finally {
    exporting.value = false;
  }
}

async function handleAcceptanceRun() {
  if (!detail.value?.id) return;
  accepting.value = true;
  acceptanceResult.value = null;
  try {
    const result = await runDocumentSkillAcceptance(detail.value.id);
    acceptanceResult.value = result;
    if (result.failed > 0) {
      ElMessage.warning(`验收完成：${result.passed}/${result.total} 通过，${result.failed} 失败`);
    } else if (result.total === 0) {
      ElMessage.info(result.warnings?.[0] || '暂无验收用例');
    } else {
      ElMessage.success(`验收全部通过（${result.passed}/${result.total}）`);
    }
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  } finally {
    accepting.value = false;
  }
}

async function handleImport() {
  importWarnings.value = [];
  if (importTab.value === 'package' && !importForm.packageContent.trim() && !importZipFile.value) {
    ElMessage.warning('请上传标准 Skill ZIP / JSON，或粘贴 JSON');
    return;
  }
  if (importTab.value === 'clone' && !importForm.sourceSkillId.trim()) {
    ElMessage.warning('请填写源 Skill ID');
    return;
  }
  saving.value = true;
  try {
    const result =
      importTab.value === 'package' && importZipFile.value
        ? await importDocumentSkillPackageFile(props.projectId, importZipFile.value, {
            code: importForm.code.trim() || undefined,
            name: importForm.name.trim() || undefined,
          })
        : await importDocumentSkill(
            props.projectId,
            importTab.value === 'package'
              ? {
                  packageContent: importForm.packageContent.trim(),
                  code: importForm.code.trim() || undefined,
                  name: importForm.name.trim() || undefined,
                }
              : {
                  sourceSkillId: importForm.sourceSkillId.trim(),
                  code: importForm.code.trim() || undefined,
                  name: importForm.name.trim() || undefined,
                },
          );
    importWarnings.value = result.warnings || [];
    ElMessage.success(
      importWarnings.value.length
        ? `导入成功（Draft，${importWarnings.value.length} 条校验提示）`
        : '导入成功（Draft）',
    );
    await reload();
    await selectSkill(result.skill.id);
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  } finally {
    saving.value = false;
  }
}

async function handlePublish() {
  if (!detail.value) return;
  try {
    await publishDocumentSkill(detail.value.id);
    ElMessage.success('已发布');
    await reload();
    await selectSkill(detail.value.id);
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  }
}

async function handleDisable() {
  if (!detail.value) return;
  try {
    await updateDocumentSkillStatus(detail.value.id, 'disabled');
    ElMessage.success('已停用');
    await reload();
    await selectSkill(detail.value.id);
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  }
}

onMounted(async () => {
  await reload();
  if (props.initialMode === 'create') {
    startCreateViaChat();
    return;
  }
  if (props.initialMode === 'import') {
    startImport();
  }
});
</script>

<style scoped>
.skill-studio-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(24, 25, 26, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.skill-studio {
  width: min(980px, 100%);
  height: min(680px, 92vh);
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
}
.studio-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid #ececec;
}
.studio-header h2 {
  margin: 0;
  font-size: 18px;
}
.studio-header p {
  margin: 6px 0 0;
  color: #6b6f76;
  font-size: 13px;
}
.close-btn {
  border: none;
  background: transparent;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
}
.studio-body {
  flex: 1;
  display: grid;
  grid-template-columns: 280px 1fr;
  min-height: 0;
}
.skill-list-pane {
  border-right: 1px solid #ececec;
  padding: 16px;
  overflow: auto;
}
.list-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.list-actions button,
.form-actions button,
.import-tabs button {
  border: 1px solid #d8d8d8;
  background: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
}
.list-actions .primary,
.form-actions .primary {
  background: #1f6feb;
  border-color: #1f6feb;
  color: #fff;
}
.skill-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.skill-list li {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.skill-list li.active,
.skill-list li:hover {
  background: #f3f7ff;
}
.skill-list span {
  color: #6b6f76;
  font-size: 12px;
}
.empty,
.hint,
.meta {
  color: #6b6f76;
  font-size: 13px;
}
.skill-detail-pane {
  padding: 20px 24px;
  overflow: auto;
}
.skill-detail-pane h3 {
  margin: 0 0 12px;
}
.section-title {
  margin: 16px 0 8px;
  font-size: 13px;
  color: #3c4045;
}
.skill-detail-pane label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #3c4045;
}
.skill-detail-pane input,
.skill-detail-pane textarea,
.skill-detail-pane select {
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  padding: 8px 10px;
  font: inherit;
}
.prompt-block {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 12px;
  white-space: pre-wrap;
  font-size: 12px;
}
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.warn-box {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff7e8;
  color: #d35a00;
  font-size: 12px;
  line-height: 1.5;
}
.accept-box {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f3f7ff;
  color: #1f2a44;
  font-size: 12px;
  line-height: 1.5;
}
.accept-box ul {
  margin: 8px 0 0;
  padding-left: 0;
  list-style: none;
}
.accept-box li {
  margin-bottom: 4px;
}
.case-status {
  display: inline-block;
  min-width: 52px;
  margin-right: 6px;
  font-weight: 600;
}
.case-status.passed {
  color: #0a7a3e;
}
.case-status.failed {
  color: #c0392b;
}
.case-status.skipped {
  color: #8a9099;
}
.package-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.badge.ok {
  background: #e8f8ef;
  color: #0a7a3e;
}
.badge.warn {
  background: #fff7e8;
  color: #d35a00;
}
.import-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.import-tabs button.active {
  background: #ecf2fe;
  border-color: #266fe8;
  color: #0052d9;
}
.file-upload input[type='file'] {
  margin-top: 6px;
}
</style>
